import { AsyncPipe } from '@angular/common'
import { ChangeDetectorRef, Component, HostBinding, inject, Input, OnInit } from '@angular/core'
import { Router, RouterLink, RouterLinkActive } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { Badge } from 'primeng/badge'
import { Button } from 'primeng/button'
import { BehaviorSubject } from 'rxjs'

import { Action, AppFeatures, Resource, UserAuthMethod } from '@ti/contracts'
import { AuthSession, Profile } from '@ti/core/auth/services'
import { AppCommonModule } from '@ti/core/common/common.module'
import { CheckPricingPlanDirective, ExperimentalFeatureDirective } from '@ti/core/common/directives'
import { AccessControl, DeviceService } from '@ti/core/common/services'
import { BrandingData } from '@ti/core/common/services/branding.service'
import { injectDestroy$ } from '@ti/core/common/utils'
import { CONFIG } from '@ti/core/contracts/config'
import {
  BeforeInstallPromptEvent,
  iMenuElement,
  SIDEBAR_BADGES_MODEL,
} from '@ti/core/contracts/sidebar'
import { AlertsIconComponent } from '@ti/core/icons/alerts'
import { BarChart2IconComponent } from '@ti/core/icons/bar-chart-2'
import { ChevronIconComponent } from '@ti/core/icons/chevron'
import { DownloadIconComponent } from '@ti/core/icons/download'
import { FavoritesIconComponent } from '@ti/core/icons/favorites'
import { GeofenceIconComponent } from '@ti/core/icons/geofence'
import { MessagesIconComponent } from '@ti/core/icons/messages'
import { PlusIconComponent } from '@ti/core/icons/plus'
import { ReportsIconComponent } from '@ti/core/icons/reports'
import { RouteIconComponent } from '@ti/core/icons/route'
import { SettingsIconComponent } from '@ti/core/icons/settings'
import { TruckIconComponent } from '@ti/core/icons/truck'
import { VideoIconComponent } from '@ti/core/icons/video'
import { WrenchIconComponent } from '@ti/core/icons/wrench'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { LangSwitchDialogComponent } from '@ti/core/ui-kit/i18n/components/lang-switch-dialog/lang-switch-dialog.component'
import { AvatarComponent } from '@ti/core/ui-kit/layout/components/avatar'
import { DynamicHostComponent } from '@ti/core/ui-kit/layout/components/dynamic-host'
import { FeedbackButtonComponent } from '@ti/core/ui-kit/layout/components/feedback-button'
import { PwaInstallGuideComponent } from '@ti/core/ui-kit/layout/components/pwa-install-guide'
import { MenuTooltipDirective } from '@ti/core/ui-kit/layout/directives/menu-tooltip.directive'
import { ScrollDetectorDirective } from '@ti/core/ui-kit/layout/directives/scroll-detector.directive'
import { TooltipDirective } from '@ti/core/ui-kit/layout/directives/tooltip.directive'
import { SidebarService } from '@ti/core/ui-kit/layout/services/sidebar.service'
import { AssistantMenuButtonComponent } from '@ti/shared/assistant/components/menu-button/menu-button.component'
import { PersonalMenuComponent } from '@ti/shared/user/components/personal-menu/personal-menu.component'
import { UserSettingsDialogComponent } from '@ti/shared/user/components/user-settings-dialog/user-settings-dialog.component'
import { BaseUserSettingsModel } from '@ti/shared/user/models/base-user-settings.model'

import { FleetUserSettingsModel } from '../models/user-settings.model'

@Component({
  selector: 'app-sidebar',
  templateUrl: 'sidebar.component.html',
  styleUrls: ['sidebar.component.scss'],
  providers: [{ provide: BaseUserSettingsModel, useClass: FleetUserSettingsModel }],
  imports: [
    AppCommonModule,
    AsyncPipe,
    AvatarComponent,
    Badge,
    Button,
    CheckPricingPlanDirective,
    ChevronIconComponent,
    DownloadIconComponent,
    DynamicHostComponent,
    ExperimentalFeatureDirective,
    FeedbackButtonComponent,
    LangSwitchDialogComponent,
    MenuTooltipDirective,
    PersonalMenuComponent,
    PwaInstallGuideComponent,
    RouterLink,
    RouterLinkActive,
    ScrollDetectorDirective,
    SettingsIconComponent,
    AssistantMenuButtonComponent,
    TooltipDirective,
    TranslatePipe,
    UserSettingsDialogComponent,
  ],
})
export class SidebarComponent implements OnInit {
  @Input()
  @HostBinding('class.collapsed')
  public collapsed = false

  @Input()
  public brandingData?: BrandingData | null

  protected readonly accessControl = inject(AccessControl)
  protected readonly authSession = inject(AuthSession)
  protected readonly badgesModel = inject(SIDEBAR_BADGES_MODEL)
  protected readonly config = inject(CONFIG)
  protected readonly deviceService = inject(DeviceService)
  protected readonly languageService = inject(LanguageService)
  protected readonly profile = inject(Profile)
  protected readonly router = inject(Router)
  protected readonly sidebarService = inject(SidebarService)
  protected readonly changeDetectorRef = inject(ChangeDetectorRef)
  protected readonly destroy$ = injectDestroy$()

  protected readonly installPwaEvent$ = new BehaviorSubject<BeforeInstallPromptEvent | undefined>(
    undefined,
  )

  protected TYPES = { UserAuthMethod }

  protected readonly menuElements: iMenuElement[] = [
    {
      name: 'fleet.menu.bookmarks',
      icon: FavoritesIconComponent,
      path: ['bookmarks'],
      requirePricingPlan: [AppFeatures.Bookmarks],
      checkAccess: [Resource.Bookmarks, Action.View],
    },
    {
      name: 'fleet.menu.dashboard',
      icon: BarChart2IconComponent,
      path: ['dashboard'],
      requirePricingPlan: [AppFeatures.Dashboard],
      checkAccess: [Resource.Dashboard, Action.View],
    },
    {
      name: 'fleet.menu.fleet',
      icon: TruckIconComponent,
      path: ['fleet'],
      checkAccess: [Resource.Vehicle, Action.View],
    },
    {
      name: 'fleet.menu.alters',
      icon: AlertsIconComponent,
      path: ['alerts'],
      checkAccess: [Resource.Alert, Action.View],
      actionButton: {
        name: 'fleet.menu.alerts_setup',
        path: ['alerts', 'setup'],
        icon: PlusIconComponent,
        checkAccess: [Resource.Alert, Action.Create],
      },
      children: [
        {
          icon: PlusIconComponent,
          name: 'fleet.menu.alerts_setup',
          path: ['alerts', 'setup'],
          cssClass: 'has-bottom-separator',
          checkAccess: [Resource.Alert, Action.Create],
        },
        {
          name: 'fleet.menu.alerts_triggered',
          path: ['alerts', 'triggered'],
          counterKey: 'unreviewedAlerts',
          checkAccess: [Resource.AlertWorkflow, Action.View],
        },
        {
          name: 'fleet.menu.alerts_created',
          path: ['alerts', 'manage'],
          checkAccess: [Resource.Alert, Action.View],
        },
      ],
    },
    {
      name: 'fleet.menu.video',
      icon: VideoIconComponent,
      path: ['video'],
      requirePricingPlan: [AppFeatures.Video],
      checkAccess: [Resource.Media, Action.View],
      children: [
        {
          name: 'fleet.menu.video_request',
          path: ['video', 'request'],
        },
        {
          name: 'fleet.menu.video_library',
          path: ['video', 'library'],
          counterKey: 'newMediaLibraryItems',
        },
        {
          name: 'fleet.menu.video_stream',
          path: ['video', 'stream'],
        },
      ],
    },
    {
      name: 'fleet.menu.dispatch',
      icon: RouteIconComponent,
      path: ['dispatch'],
      requirePricingPlan: [AppFeatures.Dispatch],
    },
    {
      name: 'fleet.menu.maintenance',
      icon: WrenchIconComponent,
      path: ['maintenance'],
      requirePricingPlan: [AppFeatures.Maintenance],
      checkAccess: [Resource.Maintenance, Action.View],
      children: [
        {
          name: 'fleet.menu.maintenance_overdue',
          path: ['maintenance', 'scheduled-services'],
          counterKey: 'overdueAndSoonVehicleServicesCount',
        },
        {
          name: 'fleet.menu.maintenance_status',
          path: ['maintenance', 'status'],
        },
        {
          name: 'fleet.menu.maintenance_reports',
          path: ['maintenance', 'reports'],
          counterKey: 'needsAttentionInspectionReportsCount',
        },
        {
          name: 'fleet.menu.maintenance_services',
          path: ['maintenance', 'services-performed'],
        },
        {
          name: 'fleet.menu.maintenance_schedules',
          path: ['maintenance', 'schedules'],
        },
      ],
    },
    {
      name: 'fleet.menu.chat-with-drivers',
      icon: MessagesIconComponent,
      path: ['chat'],
      counterKey: 'chatTotalUnreadCount',
      checkAccess: [Resource.Driver, Action.View],
      requirePricingPlan: [AppFeatures.DriverAccess],
    },
    {
      name: 'fleet.menu.reports',
      icon: ReportsIconComponent,
      path: ['reports'],
      checkAccess: [Resource.Reports, Action.View],
    },
    {
      name: 'fleet.menu.locations',
      icon: GeofenceIconComponent,
      path: ['locations'],
      checkAccess: [Resource.Location, Action.View],
    },
  ]

  protected readonly IS_NATIVE_PLATFORM = this.deviceService.getPlatform() !== 'web'

  public async ngOnInit() {
    this.badgesModel.init()
    this.initPWAInstall()
  }

  protected initPWAInstall() {
    const listener = (event: Event) => {
      this.installPwaEvent$.next(event as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', listener)
    this.destroy$.subscribe(() => window.removeEventListener('beforeinstallprompt', listener))
  }

  protected promptPWAInstall() {
    if (this.installPwaEvent$.value) {
      this.installPwaEvent$.value.prompt().then((result) => {
        if (result.outcome === 'accepted') {
          this.installPwaEvent$.next(undefined)
        }
      })
    }
  }

  protected toggleElementExpanded(element: iMenuElement) {
    element.isExpanded = !element.isExpanded
    this.changeDetectorRef.markForCheck()
  }
}
